import { Contract, SharedProps } from '@/types'
import { Head, Link, router, usePage } from '@inertiajs/react'
import { ActionIcon, Badge, Button, Group, Menu, Paper, Stack, Table, TextInput, Title } from '@mantine/core'
import { useIntersection } from '@mantine/hooks'
import { IconDotsVertical, IconPencil, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react'
import { useEffect, useMemo, useState } from 'react'

const PAGE_SIZE = 20

export default function ContractsPage({ contracts }: { contracts: Contract[] }) {
  const { current_organization } = usePage<SharedProps>().props
  const [searchQuery, setSearchQuery] = useState('')
  const [displayLimit, setDisplayLimit] = useState(PAGE_SIZE)

  const filteredContracts = useMemo(() => {
    if (!searchQuery.trim()) return contracts

    const normalizedQuery = searchQuery.toLowerCase().trim()
    const queryTerms = normalizedQuery.split(/\s+/).filter(Boolean)

    return contracts.filter((contract) => {
      const name = contract.customer_name?.toLowerCase() || ''
      const phone = contract.customer_phone_number?.replace(/\s/g, '').toLowerCase() || ''
      const email = contract.customer_email?.toLowerCase() || ''

      return queryTerms.every((term) => name.includes(term) || phone.includes(term) || email.includes(term))
    })
  }, [contracts, searchQuery])

  useEffect(() => setDisplayLimit(PAGE_SIZE), [searchQuery])

  const displayedContracts = filteredContracts.slice(0, displayLimit)
  const hasMore = filteredContracts.length > displayLimit
  const handleShowMore = () => setDisplayLimit((previous) => previous + PAGE_SIZE)
  const { ref: loadMoreRef, entry } = useIntersection()

  useEffect(() => {
    if (entry?.isIntersecting && hasMore) handleShowMore()
  }, [entry?.isIntersecting, hasMore])

  const handleDelete = (contract: Contract) => {
    if (confirm(`Êtes-vous sûr de vouloir supprimer le contrat avec ${contract.customer_name} ?`)) {
      router.delete(`/organizations/${current_organization?.slug}/contracts/${contract.id}`)
    }
  }

  return (
    <>
      <Head title={`Contrats - ${current_organization?.name}`} />

      <Stack>
        <Group justify="space-between" align="center">
          <Title>Contrats</Title>
          <Button
            component={Link}
            href={`/organizations/${current_organization?.slug}/contracts/new`}
            leftSection={<IconPlus size={18} />}
          >
            Nouveau contrat
          </Button>
        </Group>

        <Group align="flex-end">
          <TextInput
            placeholder="Rechercher par nom, téléphone ou email..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            style={{ flex: 1 }}
          />
        </Group>

        <Paper withBorder radius="md" p="md">
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Société</Table.Th>
                <Table.Th>Téléphone</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Collecte</Table.Th>
                <Table.Th style={{ width: 40 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {displayedContracts.map((contract) => (
                <Table.Tr
                  key={contract.id}
                  onClick={() => router.get(`/organizations/${current_organization?.slug}/contracts/${contract.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <Table.Td>{contract.customer_name}</Table.Td>
                  <Table.Td>{contract.customer_phone_number || '—'}</Table.Td>
                  <Table.Td>{contract.customer_email || '—'}</Table.Td>
                  <Table.Td>
                    {contract.collected_by_provider ? (
                      <Badge variant="light" color="blue">
                        Coursier
                      </Badge>
                    ) : (
                      <Badge variant="light" color="gray">
                        Sur place
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td onClick={(event) => event.stopPropagation()}>
                    <Menu shadow="md" width={200} position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray">
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>

                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconPencil size={14} />}
                          onClick={() =>
                            router.get(`/organizations/${current_organization?.slug}/contracts/${contract.id}/edit`)
                          }
                        >
                          Modifier
                        </Menu.Item>
                        <Menu.Item
                          color="red"
                          leftSection={<IconTrash size={14} />}
                          onClick={() => handleDelete(contract)}
                        >
                          Supprimer
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>

        {hasMore && (
          <>
            <div ref={loadMoreRef} style={{ height: 1 }} />
            <Button variant="subtle" onClick={handleShowMore} fullWidth>
              Afficher plus
            </Button>
          </>
        )}
      </Stack>
    </>
  )
}
